/**
 * React Hooks for Inventory Items
 *
 * This module provides custom hooks for managing inventory state and operations
 * in React components using React Query for caching and synchronization.
 *
 * Query hooks use directApiClient for direct API calls (faster, no server action overhead).
 * Mutation hooks still use server actions for revalidation support.
 */

'use client';

import { useCallback } from 'react';
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { FRESH_DATA_CONFIG, REFERENCE_DATA_CONFIG } from '../query-config';
import { createMutationHook, createOptimisticContext } from '../mutation-utils';
import { getDependencies } from '../query-dependencies';
import { directApiClient } from '@/lib/api/direct-client';
import { inventoryKeys, institutionKeys } from '@/lib/query-keys';
import {
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  setValueConfirmation,
  clearValueConfirmation,
  fetchValueConfirmationSources
} from './client';
import { applyOptimisticInventoryUpdate, enrichInventoryItem } from './utils';
import { formatCurrencyWithSign } from '@/lib/formatters/australian-formatters';
import type {
  InventoryItem,
  InventoryItemTypeMetadata,
  InventoryItemsListResponse,
  CreateInventoryItemRequest,
  UpdateInventoryItemRequest,
  InventoryItemFilters,
  CurrentViewResponseDto,
  TodayGroup,
  SetValueConfirmationRequest,
  ValueConfirmationResponse,
  ValueConfirmationSourcesResponse
} from './types';

// Query Hooks

/**
 * Hook to fetch all inventory items for a matter
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useInventoryItems(
  matterId: number,
  filters?: InventoryItemFilters,
  options?: Omit<
    UseQueryOptions<InventoryItemsListResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: inventoryKeys.list(matterId, filters),
    queryFn: async () => {
      // Always fetch all items including archived by default
      const filtersWithAll = {
        ...filters,
        status: 'all' as const
      };

      // Direct API call via directApiClient
      const items = await directApiClient.get<InventoryItem[]>(
        `/matters/${matterId}/inventory-items`,
        { params: filtersWithAll }
      );

      // Build response with enriched items
      const enrichedItems = items.map(enrichInventoryItem);
      const totalValue = enrichedItems.reduce(
        (sum, item) => sum + (item.value || 0),
        0
      );

      return {
        items: enrichedItems,
        total: enrichedItems.length,
        totalValue
      } as InventoryItemsListResponse;
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch the Current View (Outstanding / Distributed two-section model)
 * for a matter — backs the DoD/Current toggle's Current View. The DTO is
 * consumed directly (the BFF resolves balance, totals, proceeds labels, and
 * status tags server-side). The endpoint serves unconditionally.
 */
export function useInventoryCurrentView(
  matterId: number,
  options?: Omit<
    UseQueryOptions<CurrentViewResponseDto>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: inventoryKeys.currentView(matterId),
    queryFn: async () => {
      return directApiClient.get<CurrentViewResponseDto>(
        `/matters/${matterId}/inventory-items/current-view`
      );
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch a single inventory item
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useInventoryItem(
  matterId: number,
  inventoryItemId: number,
  options?: Omit<UseQueryOptions<InventoryItem>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: inventoryKeys.detail(matterId, inventoryItemId),
    queryFn: async () => {
      // Direct API call via directApiClient (bypasses proxy)
      const item = await directApiClient.get<InventoryItem>(
        `/matters/${matterId}/inventory-items/${inventoryItemId}`
      );
      return enrichInventoryItem(item);
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch the institution-response attachments the manual value-
 * confirmation picker can cite as a source for an item (LF-5393). Callers gate
 * with `enabled` so it only fires while the picker is actually open.
 */
export function useValueConfirmationSources(
  matterId: number,
  inventoryItemId: number,
  options?: Omit<
    UseQueryOptions<ValueConfirmationSourcesResponse>,
    'queryKey' | 'queryFn'
  >
) {
  /*
    The item has to be PERSISTED, and the hook owns that rather than each
    caller (VALE-1523).

    Callers were gating on "an id exists" — `!!itemId`, `itemId != null` — and
    the repo's sentinel for an unsaved record is `-1`, which passes both. So an
    inventory item being created from an AI extraction fired
    `/inventory-items/-1/value-confirmation-sources` on every render of the
    review step: a request that can only ever 404, six of them across one
    review-and-apply walk. There is no such thing as an institution's reply
    about an item that does not exist yet, so this is a precondition of the
    query itself, not a preference of whoever calls it.
  */
  const hasPersistedIds = matterId > 0 && inventoryItemId > 0;

  return useQuery({
    queryKey: inventoryKeys.valueConfirmationSources(matterId, inventoryItemId),
    queryFn: async () =>
      fetchValueConfirmationSources(matterId, inventoryItemId),
    ...FRESH_DATA_CONFIG,
    ...options,
    enabled: (options?.enabled ?? true) && hasPersistedIds
  });
}

/**
 * Hook to fetch all inventory item types
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useInventoryItemTypes(
  options?: Omit<
    UseQueryOptions<InventoryItemTypeMetadata[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: inventoryKeys.types(),
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<InventoryItemTypeMetadata[]>(
        '/inventory-item-types'
      );
    },
    ...REFERENCE_DATA_CONFIG,
    ...options
  });
}

/**
 * Shared shape for inventory summary metrics (used at top level and per estate/non-estate)
 */
export interface InventoryItemsSummaryMetrics {
  totalAssets: number;
  totalLiabilities: number;
  netEstate: number;
  assetCount: number;
  liabilityCount: number;
  settledLiabilities: number;
  outstandingLiabilities: number;
}

/**
 * Collection metrics — status-based asset breakdowns, gap count, realised totals
 */
export interface InventoryItemsCollectionMetrics {
  collectedAssetsTotal: number;
  collectedAssetsCount: number;
  pendingAssetsTotal: number;
  pendingAssetsCount: number;
  collectionDataGapCount: number;
  realisedAssetsTotal: number;
  realisedLiabilitiesTotal: number;
}

/**
 * Summary response from the inventory items summary endpoint
 */
export interface InventoryItemsSummary extends InventoryItemsSummaryMetrics {
  estate: InventoryItemsSummaryMetrics;
  nonEstate: InventoryItemsSummaryMetrics;
  collectionMetrics: InventoryItemsCollectionMetrics;
}

// Mutation Hooks

/**
 * Hook to create a new inventory item
 */
export function useCreateInventoryItem(
  matterId: number,
  options?: UseMutationOptions<InventoryItem, Error, CreateInventoryItemRequest>
) {
  const useHook = createMutationHook<InventoryItem, CreateInventoryItemRequest>(
    {
      mutationFn: async (data) => createInventoryItem(matterId, data),
      invalidateKeys: getDependencies('inventory-item', matterId),
      onSuccessMessage: (data) => {
        const enrichedItem = enrichInventoryItem(data);
        // Build a meaningful description with fallbacks
        let displayName = enrichedItem.description;

        // Skip descriptions that are empty or generic "Unknown Asset"
        if (!displayName || displayName === 'Unknown Asset') {
          // Try institution name
          if (enrichedItem.institution?.name) {
            displayName = enrichedItem.institution.name;
          }
          // Fall back to type name
          else if (
            typeof enrichedItem.type === 'object' &&
            enrichedItem.type?.name
          ) {
            displayName = enrichedItem.type.name;
          }
          // Fall back to type slug formatted
          else if (typeof enrichedItem.type === 'string') {
            displayName = enrichedItem.type
              .replace(/-/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase());
          }
          // Last resort
          else {
            displayName = 'inventory item';
          }
        }

        return `Added ${displayName} - ${formatCurrencyWithSign(enrichedItem.value || 0)}`;
      },
      onErrorMessage: 'Failed to create inventory item'
    }
  );

  return useHook(options);
}

/**
 * Hook to update an inventory item with optimistic updates
 *
 * IMPORTANT: When status changes from 'draft' to another status, the backend
 * assigns an estateReference number. This hook ensures the returned item data
 * (including estateReference) is merged into the cache after mutation success.
 */
export function useUpdateInventoryItem(
  matterId: number,
  options?: UseMutationOptions<
    InventoryItem,
    Error,
    {
      inventoryItemId: number;
      updates: UpdateInventoryItemRequest;
    },
    { snapshot: InventoryItemsListResponse | undefined }
  >,
  /**
   * **Silences the success *and* failure toast** — for the attribute drawer's
   * commit-on-blur path (VALE-75 / [INVDRAW-4]).
   *
   * Both halves are deliberate, and both are wrong the other way round on that
   * surface:
   *
   * · **Success.** This writes one field per blur, so the default
   *   `'Inventory item updated successfully'` would fire on every field the
   *   reader leaves — a toast per keystroke-commit. The record updating in front
   *   of them is the confirmation.
   * · **Failure.** The attribute grid reports a rejected commit *on the row*, and
   *   re-opens it with the typing intact (`components/attributes/CLAUDE.md` §6).
   *   A toast as well would report the same failure twice, in two places, one of
   *   which the reader has to dismiss.
   *
   * It stays **rejecting** either way — `mutateAsync` rethrows, which is what the
   * grid's failure path is built on. Silencing the toast is not swallowing the
   * error.
   *
   * Mirrors `useGenericUpdateContact`'s third parameter exactly (LF-6306); see
   * {@link useInventoryService}'s `commitItemFields`.
   */
  { showToast = true }: { showToast?: boolean } = {}
) {
  const queryClient = useQueryClient();

  const useHook = createMutationHook<
    InventoryItem,
    { inventoryItemId: number; updates: UpdateInventoryItemRequest },
    {
      snapshot: InventoryItemsListResponse | undefined;
      originalStatus?: string;
    }
  >({
    mutationFn: async ({ inventoryItemId, updates }) =>
      updateInventoryItem(matterId, inventoryItemId, updates),
    invalidateKeys: getDependencies('inventory-item', matterId),
    optimisticUpdate: ({ inventoryItemId, updates }) => {
      const cacheKey = inventoryKeys.list(matterId);
      const context = createOptimisticContext<InventoryItemsListResponse>(
        queryClient,
        cacheKey
      );

      // Find the original item to get its status before update
      const originalItem = context.snapshot?.items.find(
        (item) => item.id === inventoryItemId
      );
      const originalStatus = originalItem?.status;

      // Apply optimistic update to the main cache
      context.update((old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.id === inventoryItemId
              ? applyOptimisticInventoryUpdate(item, updates)
              : item
          )
        };
      });

      // Also update all institution-specific caches
      const allCacheData = queryClient.getQueriesData({
        queryKey: inventoryKeys.forMatter(matterId),
        exact: false
      });

      allCacheData.forEach(([queryKey, data]) => {
        if (data && typeof data === 'object' && 'items' in data) {
          const typedData = data as InventoryItemsListResponse;
          queryClient.setQueryData(queryKey, {
            ...typedData,
            items: typedData.items.map((item) =>
              item.id === inventoryItemId
                ? applyOptimisticInventoryUpdate(item, updates)
                : item
            )
          });
        }
      });

      /*
        The single-item cache too (VALE-1505). `inventoryKeys.detail` is not under
        `forMatter`, so the Title Search Review's no-changes card, which reads
        the item through `useInventoryItem`, got only the invalidation, and a
        committed field repainted its previous value until the refetch landed.
      */
      queryClient.setQueryData<InventoryItem>(
        inventoryKeys.detail(matterId, inventoryItemId),
        (old) => (old ? applyOptimisticInventoryUpdate(old, updates) : old)
      );

      return { snapshot: context.snapshot, originalStatus };
    },
    onSuccessMessage: (data, variables, context) => {
      // Only show status-specific message if status actually changed
      if (data.status && context?.originalStatus !== data.status) {
        const statusMessages: Record<string, string> = {
          identified: 'Item marked as identified',
          notified: 'Institution notified',
          collected: 'Item marked as collected',
          'not-held': 'Item marked as not held',
          draft: 'Item moved back to draft',
          archived: 'Item archived'
        };
        return statusMessages[data.status] || 'Status updated successfully';
      }
      return 'Inventory item updated successfully';
    },
    onErrorMessage: 'Failed to update inventory item',
    showToast
  });

  // Wrap the hook to add custom onSuccess behavior that updates cache with full item data
  return useHook({
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      // Enrich the returned item with computed fields
      const enrichedItem = enrichInventoryItem(data);

      // Update all inventory list caches with the FULL item data from backend
      // This is critical when status changes from 'draft' because the backend
      // assigns the estateReference number which isn't in the optimistic update
      const allCacheData = queryClient.getQueriesData({
        queryKey: inventoryKeys.forMatter(matterId),
        exact: false
      });

      allCacheData.forEach(([queryKey, cacheData]) => {
        if (
          cacheData &&
          typeof cacheData === 'object' &&
          'items' in cacheData
        ) {
          const typedData = cacheData as InventoryItemsListResponse;
          queryClient.setQueryData(queryKey, {
            ...typedData,
            items: typedData.items.map((item) =>
              item.id === variables.inventoryItemId
                ? enrichedItem // Replace with full item data from backend
                : item
            )
          });
        }
      });

      // Also update the main cache without filters
      const mainCacheKey = inventoryKeys.list(matterId);
      const mainCacheData =
        queryClient.getQueryData<InventoryItemsListResponse>(mainCacheKey);
      if (mainCacheData) {
        queryClient.setQueryData(mainCacheKey, {
          ...mainCacheData,
          items: mainCacheData.items.map((item) =>
            item.id === variables.inventoryItemId ? enrichedItem : item
          )
        });
      }

      // Also update the individual item cache if it exists
      const itemCacheKey = inventoryKeys.detail(
        matterId,
        variables.inventoryItemId
      );
      queryClient.setQueryData(itemCacheKey, enrichedItem);

      // Invalidate institution-specific caches if the item has an institution
      // This ensures the institution's status summary and item counts refresh
      const institutionId =
        enrichedItem.institutionId || enrichedItem.institution?.id;
      if (institutionId) {
        // Invalidate institution status cache (affects institution card status display)
        await queryClient.invalidateQueries({
          queryKey: institutionKeys.status(matterId, institutionId),
          exact: false
        });

        // Invalidate institution-filtered inventory cache
        await queryClient.invalidateQueries({
          queryKey: inventoryKeys.list(matterId, { institutionId }),
          exact: false
        });
      }

      // Also invalidate the main matter-institutions list to refresh aggregated data
      await queryClient.invalidateQueries({
        queryKey: institutionKeys.forMatter(matterId),
        exact: false
      });

      // Call the original onSuccess if provided
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    }
  });
}

/**
 * Hook to delete an inventory item with optimistic removal
 */
export function useDeleteInventoryItem(
  matterId: number,
  options?: UseMutationOptions<
    void,
    Error,
    number,
    {
      snapshot: InventoryItemsListResponse | undefined;
      deletedItem?: InventoryItem;
    }
  >
) {
  const queryClient = useQueryClient();

  const useHook = createMutationHook<
    void,
    number,
    {
      snapshot: InventoryItemsListResponse | undefined;
      deletedItem?: InventoryItem;
    }
  >({
    mutationFn: async (inventoryItemId) =>
      deleteInventoryItem(matterId, inventoryItemId),
    invalidateKeys: getDependencies('inventory-item', matterId),
    optimisticUpdate: (inventoryItemId) => {
      const cacheKey = inventoryKeys.list(matterId);
      const context = createOptimisticContext<InventoryItemsListResponse>(
        queryClient,
        cacheKey
      );

      // Find the item being deleted for context
      const deletedItem = context.snapshot?.items.find(
        (item) => item.id === inventoryItemId
      );

      // Optimistically remove the item from the main cache
      context.update((old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.id !== inventoryItemId),
          total: Math.max(0, old.total - 1),
          totalValue:
            old.totalValue -
            (old.items.find((item) => item.id === inventoryItemId)?.value || 0)
        };
      });

      // Also update all institution-specific caches
      const allCacheData = queryClient.getQueriesData({
        queryKey: inventoryKeys.forMatter(matterId),
        exact: false
      });

      allCacheData.forEach(([queryKey, data]) => {
        if (data && typeof data === 'object' && 'items' in data) {
          const typedData = data as InventoryItemsListResponse;
          const itemToRemove = typedData.items.find(
            (item) => item.id === inventoryItemId
          );
          if (itemToRemove) {
            queryClient.setQueryData(queryKey, {
              ...typedData,
              items: typedData.items.filter(
                (item) => item.id !== inventoryItemId
              ),
              total: Math.max(0, typedData.total - 1),
              totalValue: typedData.totalValue - (itemToRemove.value || 0)
            });
          }
        }
      });

      // Also remove the row from the Current View cache (LF-5360). It's a
      // separate BFF response ({ outstanding, distributed }, no top-level
      // `items`), so the loop above never touches it — without this the row
      // only disappears once the invalidated current-view query refetches,
      // a full extra network round-trip later.
      const currentViewKey = inventoryKeys.currentView(matterId);
      const currentViewData =
        queryClient.getQueryData<CurrentViewResponseDto>(currentViewKey);
      if (currentViewData) {
        const removeFromGroup = (group: TodayGroup): TodayGroup => {
          const row = group.rows.find(
            (r) => r.inventoryItemId === inventoryItemId
          );
          if (!row) return group;
          return {
            rows: group.rows.filter(
              (r) => r.inventoryItemId !== inventoryItemId
            ),
            dodTotal: group.dodTotal - (row.dodValue ?? 0),
            currentTotal: group.currentTotal - (row.currentValue ?? 0)
          };
        };

        const estateAssets = removeFromGroup(
          currentViewData.outstanding.estateAssets
        );
        const estateLiabilities = removeFromGroup(
          currentViewData.outstanding.estateLiabilities
        );
        const nonEstateAssets = removeFromGroup(
          currentViewData.outstanding.nonEstateAssets
        );
        const nonEstateLiabilities = removeFromGroup(
          currentViewData.outstanding.nonEstateLiabilities
        );

        queryClient.setQueryData<CurrentViewResponseDto>(currentViewKey, {
          ...currentViewData,
          outstanding: {
            ...currentViewData.outstanding,
            estateAssets,
            estateLiabilities,
            nonEstateAssets,
            nonEstateLiabilities,
            estateNet: {
              dodNet: estateAssets.dodTotal + estateLiabilities.dodTotal,
              currentNet:
                estateAssets.currentTotal + estateLiabilities.currentTotal
            },
            nonEstateNet: {
              dodNet: nonEstateAssets.dodTotal + nonEstateLiabilities.dodTotal,
              currentNet:
                nonEstateAssets.currentTotal + nonEstateLiabilities.currentTotal
            }
          }
        });
      }

      return { snapshot: context.snapshot, deletedItem };
    },
    onSuccessMessage: () => 'Item deleted',
    onErrorMessage: 'Failed to delete inventory item'
  });

  // Wrap the hook to add custom onSuccess behavior for cache cleanup
  return useHook({
    ...options,
    onSuccess: async (data, inventoryItemId, onMutateResult, context) => {
      // Remove the individual item cache if it exists
      const itemCacheKey = inventoryKeys.detail(matterId, inventoryItemId);
      queryClient.removeQueries({ queryKey: itemCacheKey });

      // Invalidate institution-specific caches if the item had an institution
      const institutionId =
        onMutateResult?.deletedItem?.institutionId ||
        onMutateResult?.deletedItem?.institution?.id;
      if (institutionId) {
        // Invalidate institution status cache
        await queryClient.invalidateQueries({
          queryKey: institutionKeys.status(matterId, institutionId),
          exact: false
        });

        // Invalidate institution-filtered inventory cache
        await queryClient.invalidateQueries({
          queryKey: inventoryKeys.list(matterId, { institutionId }),
          exact: false
        });
      }

      // Invalidate the main matter-institutions list to refresh aggregated data
      await queryClient.invalidateQueries({
        queryKey: institutionKeys.forMatter(matterId),
        exact: false
      });

      // Call the original onSuccess if provided
      await options?.onSuccess?.(
        data,
        inventoryItemId,
        onMutateResult,
        context
      );
    }
  });
}

/**
 * Hook to confirm an item's current value against a source (LF-4356).
 * Invalidates the inventory list, detail, and current-view caches so the
 * drawer caption and the Current View indicator both refresh.
 */
export function useSetValueConfirmation(
  matterId: number,
  options?: UseMutationOptions<
    ValueConfirmationResponse,
    Error,
    { inventoryItemId: number; data: SetValueConfirmationRequest }
  >
) {
  const useHook = createMutationHook<
    ValueConfirmationResponse,
    { inventoryItemId: number; data: SetValueConfirmationRequest }
  >({
    mutationFn: async ({ inventoryItemId, data }) =>
      setValueConfirmation(matterId, inventoryItemId, data),
    invalidateKeys: getDependencies('inventory-item', matterId),
    onSuccessMessage: () => 'Current value confirmed',
    onErrorMessage: 'Failed to confirm current value'
  });

  return useHook(options);
}

/**
 * Hook to clear an item's current-value confirmation (LF-4356), reverting it to
 * the Estimate state.
 */
export function useClearValueConfirmation(
  matterId: number,
  options?: UseMutationOptions<
    ValueConfirmationResponse,
    Error,
    { inventoryItemId: number }
  >
) {
  const useHook = createMutationHook<
    ValueConfirmationResponse,
    { inventoryItemId: number }
  >({
    mutationFn: async ({ inventoryItemId }) =>
      clearValueConfirmation(matterId, inventoryItemId),
    invalidateKeys: getDependencies('inventory-item', matterId),
    onSuccessMessage: () => 'Confirmation removed',
    onErrorMessage: 'Failed to remove confirmation'
  });

  return useHook(options);
}

// Compound Hooks

/**
 * Hook providing all inventory operations for a matter
 */
export function useInventoryService(
  matterId: number,
  filters?: InventoryItemFilters,
  options?: {
    /**
     * Whether the list query may run. Defaults to enabled, so every existing
     * caller is unchanged.
     *
     * The AI-extract review surfaces (VALE-977) mount the attribute drawer over
     * *pending* items and supply their own record store, so the entity list is
     * dead weight there — and on the create-matter surface there is no matter
     * yet, which would make this a `GET /matters/0/inventory-items`. Same
     * `matterId > 0` idiom `use-inventory-block-data.ts` already uses for
     * `useMatterFiles`.
     */
    enabled?: boolean;
  }
) {
  const inventoryQuery = useInventoryItems(matterId, filters, {
    enabled: options?.enabled ?? true
  });
  const typesQuery = useInventoryItemTypes();
  const createMutation = useCreateInventoryItem(matterId);
  const updateMutation = useUpdateInventoryItem(matterId);
  const deleteMutation = useDeleteInventoryItem(matterId);
  /*
    The attribute drawer's per-field commit path (VALE-75 / [INVDRAW-4]). The
    **same** mutation as `updateMutation` — same optimistic merge, same
    invalidation set, so a committed field lands in every list cache exactly as a
    whole-form save does — with the toast silenced. See the parameter's own doc
    for why both the success and the failure toast have to go.
  */
  const commitFieldsMutation = useUpdateInventoryItem(matterId, undefined, {
    showToast: false
  });

  const createItem = useCallback(
    async (data: CreateInventoryItemRequest) => {
      return createMutation.mutateAsync(data);
    },
    [createMutation]
  );

  const updateItem = useCallback(
    async (inventoryItemId: number, updates: UpdateInventoryItemRequest) => {
      return updateMutation.mutateAsync({ inventoryItemId, updates });
    },
    [updateMutation]
  );

  /**
   * Commit one field's patch — the attribute drawer's save path (VALE-75 /
   * [INVDRAW-4]).
   *
   * Identical to {@link updateItem} but silent on success. It **rejects on
   * failure**, which is load-bearing rather than incidental: the attribute
   * treatment surfaces a failed save by the promise rejecting, and a swallowed
   * rejection reads exactly like a save that worked
   * (`components/attributes/CLAUDE.md` §6).
   *
   * A partial patch is safe by the backend's own contract, verified on
   * `develop`: `PUT /matters/:matterId/inventory-items/:id` behaves as a PATCH —
   * top-level columns go through `Object.assign` and the JSON blob is **merged**
   * (`{ ...existingItemInfo, ...incoming }`), so a body carrying one changed
   * field does not clobber its siblings. **`itemInfo: null` is the exception and
   * wipes the whole blob** — the service branches on it explicitly — so an
   * `info`-backed field must clear to `''`, never `null`.
   */
  const commitItemFields = useCallback(
    async (inventoryItemId: number, updates: UpdateInventoryItemRequest) => {
      return commitFieldsMutation.mutateAsync({ inventoryItemId, updates });
    },
    [commitFieldsMutation]
  );

  const deleteItem = useCallback(
    async (inventoryItemId: number) => {
      return deleteMutation.mutateAsync(inventoryItemId);
    },
    [deleteMutation]
  );

  const { refetch: refetchItems } = inventoryQuery;
  const { refetch: refetchTypes } = typesQuery;

  /**
   * Retry **every** query whose failure this hook reports, not just the list.
   *
   * `error` below is `inventoryQuery.error || typesQuery.error`, so the
   * Inventory tab's "Unable to load inventory items" banner is raised by either
   * read. Its "Try Again" button calls this, and while it forwarded
   * `inventoryQuery.refetch` alone a failed **types** read was unrecoverable
   * without a full page reload: `/inventory-item-types` runs on
   * `REFERENCE_DATA_CONFIG`, which sets `refetchOnMount: false` and
   * `refetchOnWindowFocus: false`, so nothing re-runs it of its own accord, and
   * the single control offered to the user re-ran the other query. A backend
   * restart fails both; the list came straight back on the first click, the
   * types query stayed in error, and the banner sat there for the life of the
   * page looking like a button that does nothing.
   *
   * The list result is what comes back, so an existing caller that reads the
   * returned `QueryObserverResult` is unchanged.
   */
  const refetch = useCallback(async () => {
    const [itemsResult] = await Promise.all([refetchItems(), refetchTypes()]);
    return itemsResult;
  }, [refetchItems, refetchTypes]);

  return {
    // Data
    items: inventoryQuery.data?.items || [],
    totalItems: inventoryQuery.data?.total || 0,
    totalValue: inventoryQuery.data?.totalValue || 0,
    byCategory: inventoryQuery.data?.byCategory || {},
    itemTypes: typesQuery.data || [],

    // Loading states
    isLoading: inventoryQuery.isLoading || typesQuery.isLoading,
    isError: inventoryQuery.isError || typesQuery.isError,
    error: inventoryQuery.error || typesQuery.error,

    // Operations
    createItem,
    updateItem,
    commitItemFields,
    deleteItem,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Refetch
    refetch
  };
}
